import React, { useRef, useEffect, useState, useCallback } from 'react';
import Chart from 'chart.js/auto'; // Using 'chart.js/auto' for Chart.js 4+

// Helper function for throttling updates
const throttle = (func, delay) => {
    let timeoutId;
    let lastArgs;
    let lastThis;
    let lastExecTime = 0;

    const throttled = function(...args) {
        const now = Date.now();
        lastArgs = args;
        lastThis = this;

        // Execute immediately if delay has passed, or queue it
        if (now - lastExecTime > delay) {
            lastExecTime = now;
            func.apply(lastThis, lastArgs);
        } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                lastExecTime = Date.now();
                func.apply(lastThis, lastArgs);
            }, delay - (now - lastExecTime));
        }
    };
    return throttled;
};


function LissajousApp({ socket }) {
    const lissajousChartRef = useRef(null); // Ref to hold the Chart.js instance
    const chartInstanceRef = useRef(null); // Ref for the actual chart object

    // State for X-axis controls
    const [waveTypeX, setWaveTypeX] = useState('sine');
    const [frequencyX, setFrequencyX] = useState(1);
    const [amplitudeX, setAmplitudeX] = useState(0.8);
    const [phaseX, setPhaseX] = useState(0);

    // State for Y-axis controls
    const [waveTypeY, setWaveTypeY] = useState('sine');
    const [frequencyY, setFrequencyY] = useState(2);
    const [amplitudeY, setAmplitudeY] = useState(0.8);
    const [phaseY, setPhaseY] = useState(90); // Initial phase for Y to make an ellipse/circle

    // Refs to track if a slider is currently being dragged
    // These are no longer used to *prevent* updates from server, but are good for debugging/future features
    const isFrequencyXDragging = useRef(false);
    const isAmplitudeXDragging = useRef(false);
    const isPhaseXDragging = useRef(false);
    const isFrequencyYDragging = useRef(false);
    const isAmplitudeYDragging = useRef(false);
    const isPhaseYDragging = useRef(false);


    // Function to send all parameters to Python backend (memoized with useCallback)
    const sendParams = useCallback(() => {
        if (socket && socket.connected) {
            const params = {
                x: {
                    type: waveTypeX,
                    frequency: parseFloat(frequencyX),
                    amplitude: parseFloat(amplitudeX),
                    phase_deg: parseFloat(phaseX)
                },
                y: {
                    type: waveTypeY,
                    frequency: parseFloat(frequencyY),
                    amplitude: parseFloat(amplitudeY),
                    phase_deg: parseFloat(phaseY)
                }
            };
            socket.emit('update_params', params);
            // console.log('Sent params:', params); // Uncomment for debugging
        } else {
            console.warn('LissajousApp: Socket not connected, delaying parameter send.');
        }
    }, [socket, waveTypeX, frequencyX, amplitudeX, phaseX, waveTypeY, frequencyY, amplitudeY, phaseY]);

    // Throttled version of sendParams, applied to slider 'input' events
    const throttledSendParams = useCallback(throttle(sendParams, 50), [sendParams]);

    // Initialize Chart.js
    const initializeChart = useCallback((xData, yData) => {
        const ctx = lissajousChartRef.current.getContext('2d');
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy(); // Destroy old chart instance if exists
        }

        const scatterData = xData.map((xVal, index) => ({
            x: xVal,
            y: yData[index]
        }));

        chartInstanceRef.current = new Chart(ctx, {
            type: 'scatter',
            data: {
                datasets: [{
                    label: 'Lissajous Pattern',
                    data: scatterData,
                    borderColor: 'rgb(0, 255, 255)',
                    backgroundColor: 'rgba(0, 255, 255, 0.1)',
                    borderWidth: 2,
                    pointRadius: 0,
                    showLine: true,
                    tension: 0.1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 0 },
                scales: {
                    x: {
                        title: { display: true, text: 'X-Axis Amplitude', color: '#e0e0e0' },
                        min: -1, max: 1,
                        ticks: { color: '#e0e0e0' },
                        grid: { color: 'rgba(255, 255, 255, 0.1)' }
                    },
                    y: {
                        title: { display: true, text: 'Y-Axis Amplitude', color: '#e0e0e0' },
                        min: -1, max: 1,
                        ticks: { color: '#e0e0e0' },
                        grid: { color: 'rgba(255, 255, 255, 0.1)' }
                    }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false }
                }
            }
        });
        // console.log("LissajousApp: Chart.js initialized."); // Uncomment for debugging
    }, []);

    // Effect for Socket.IO data reception
    useEffect(() => {
        if (!socket) return;

        const handleWaveformData = (response) => {
            // console.log('LissajousApp: Received waveform data:', response); // Uncomment for debugging

            const xData = response.x_data || [];
            const yData = response.y_data || [];
            const paramsX = response.params_x || {};
            const paramsY = response.params_y || {};

            // --- CRITICAL CHANGE FOR SLIDER STABILITY ---
            // DO NOT update slider state (frequencyX, amplitudeX, phaseX, etc.)
            // based on incoming server data if you want the sliders to "stay put".
            // Only update the wave type selects and the chart.
            setWaveTypeX(paramsX.type || 'sine'); // Always update wave type from server
            setWaveTypeY(paramsY.type || 'sine'); // Always update wave type from server

            // Always update the chart (as it reflects the server's output based on params received by backend)
            const scatterData = xData.map((xVal, index) => ({
                x: xVal,
                y: yData[index]
            }));

            if (!chartInstanceRef.current) {
                initializeChart(xData, yData);
            } else {
                chartInstanceRef.current.data.datasets[0].data = scatterData;
                chartInstanceRef.current.update();
            }
        };

        socket.on('waveform_data', handleWaveformData);

        // Cleanup: remove event listener when component unmounts or socket changes
        return () => {
            socket.off('waveform_data', handleWaveformData);
        };
    }, [socket, initializeChart, setWaveTypeX, setWaveTypeY]); // Dependencies reflect what's actually updated here.

    // Effect to send initial parameters when socket connects
    // This will send the initial state of the sliders when the app first loads.
    useEffect(() => {
        if (socket && socket.connected) {
            sendParams();
        }
    }, [socket, sendParams]); // Dependency on socket and sendParams to re-trigger if they change

    return (
        <>
            {/* Main App Content */}
            <h1 className="text-4xl font-extrabold text-teal-400 mb-8">Lissajous Pattern Generator (XY Scope)</h1>

            {/* Controls for X-Axis Buffer */}
            <div className="w-full mb-8 p-4 bg-gray-800 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-blue-400 mb-4 text-center">X-Axis Signal (Buffer 1)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="waveTypeX" className="text-lg font-semibold text-gray-200 mb-2">Wave Type X:</label>
                        <select
                            id="waveTypeX"
                            className="w-full p-2 rounded-lg bg-gray-600 border border-gray-500 text-gray-100"
                            value={waveTypeX}
                            onChange={(e) => { setWaveTypeX(e.target.value); sendParams(); }}
                        >
                            <option value="sine">Sine</option>
                            <option value="square">Square</option>
                            <option value="sawtooth">Sawtooth</option> {/* Corrected closing tag here */}
                        </select>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="frequencyX" className="text-lg font-semibold text-gray-200 mb-2">Frequency X (Hz):</label>
                        <input
                            type="range"
                            id="frequencyX"
                            min="1"
                            max="10"
                            step="1"
                            value={frequencyX}
                            onChange={(e) => { setFrequencyX(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isFrequencyXDragging.current = true}
                            onMouseUp={() => { isFrequencyXDragging.current = false; sendParams(); }} // Send final value on release
                            onTouchStart={() => isFrequencyXDragging.current = true}
                            onTouchEnd={() => { isFrequencyXDragging.current = false; sendParams(); }} // Send final value on release
                        />
                        <span id="frequencyXValue" className="mt-2 text-xl font-mono text-teal-400">{frequencyX.toFixed(1)} Hz</span>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="amplitudeX" className="text-lg font-semibold text-gray-200 mb-2">Amplitude X:</label>
                        <input
                            type="range"
                            id="amplitudeX"
                            min="0.1"
                            max="1"
                            step="0.01"
                            value={amplitudeX}
                            onChange={(e) => { setAmplitudeX(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isAmplitudeXDragging.current = true}
                            onMouseUp={() => { isAmplitudeXDragging.current = false; sendParams(); }}
                            onTouchStart={() => isAmplitudeXDragging.current = true}
                            onTouchEnd={() => { isAmplitudeXDragging.current = false; sendParams(); }}
                        />
                        <span id="amplitudeXValue" className="mt-2 text-xl font-mono text-teal-400">{amplitudeX.toFixed(2)}</span>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="phaseX" className="text-lg font-semibold text-gray-200 mb-2">Phase Shift X (Deg):</label>
                        <input
                            type="range"
                            id="phaseX"
                            min="-90"
                            max="90"
                            step="1"
                            value={phaseX}
                            onChange={(e) => { setPhaseX(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isPhaseXDragging.current = true}
                            onMouseUp={() => { isPhaseXDragging.current = false; sendParams(); }}
                            onTouchStart={() => isPhaseXDragging.current = true}
                            onTouchEnd={() => { isPhaseXDragging.current = false; sendParams(); }}
                        />
                        <span id="phaseXValue" className="mt-2 text-xl font-mono text-teal-400">{phaseX.toFixed(1)}°</span>
                    </div>
                </div>
            </div>

            {/* Controls for Y-Axis Buffer */}
            <div className="w-full mb-8 p-4 bg-gray-800 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-red-400 mb-4 text-center">Y-Axis Signal (Buffer 2)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="waveTypeY" className="text-lg font-semibold text-gray-200 mb-2">Wave Type Y:</label>
                        <select
                            id="waveTypeY"
                            className="w-full p-2 rounded-lg bg-gray-600 border border-gray-500 text-gray-100"
                            value={waveTypeY}
                            onChange={(e) => { setWaveTypeY(e.target.value); sendParams(); }}
                        >
                            <option value="sine">Sine</option>
                            <option value="square">Square</option>
                            <option value="sawtooth">Sawtooth</option> {/* Corrected closing tag here */}
                        </select>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="frequencyY" className="text-lg font-semibold text-gray-200 mb-2">Frequency Y (Hz):</label>
                        <input
                            type="range"
                            id="frequencyY"
                            min="1"
                            max="10"
                            step="1"
                            value={frequencyY}
                            onChange={(e) => { setFrequencyY(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isFrequencyYDragging.current = true}
                            onMouseUp={() => { isFrequencyYDragging.current = false; sendParams(); }}
                            onTouchStart={() => isFrequencyYDragging.current = true}
                            onTouchEnd={() => { isFrequencyYDragging.current = false; sendParams(); }}
                        />
                        <span id="frequencyYValue" className="mt-2 text-xl font-mono text-teal-400">{frequencyY.toFixed(1)} Hz</span>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="amplitudeY" className="text-lg font-semibold text-gray-200 mb-2">Amplitude Y:</label>
                        <input
                            type="range"
                            id="amplitudeY"
                            min="0.1"
                            max="1"
                            step="0.01"
                            value={amplitudeY}
                            onChange={(e) => { setAmplitudeY(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isAmplitudeYDragging.current = true}
                            onMouseUp={() => { isAmplitudeYDragging.current = false; sendParams(); }}
                            onTouchStart={() => isAmplitudeYDragging.current = true}
                            onTouchEnd={() => { isAmplitudeYDragging.current = false; sendParams(); }}
                        />
                        <span id="amplitudeYValue" className="mt-2 text-xl font-mono text-teal-400">{amplitudeY.toFixed(2)}</span>
                    </div>
                    <div className="flex flex-col items-center p-4 bg-gray-700 rounded-xl shadow-inner">
                        <label htmlFor="phaseY" className="text-lg font-semibold text-gray-200 mb-2">Phase Shift Y (Deg):</label>
                        <input
                            type="range"
                            id="phaseY"
                            min="-90"
                            max="90"
                            step="1"
                            value={phaseY}
                            onChange={(e) => { setPhaseY(parseFloat(e.target.value)); throttledSendParams(); }}
                            onMouseDown={() => isPhaseYDragging.current = true}
                            onMouseUp={() => { isPhaseYDragging.current = false; sendParams(); }}
                            onTouchStart={() => isPhaseYDragging.current = true}
                            onTouchEnd={() => { isPhaseYDragging.current = false; sendParams(); }}
                        />
                        <span id="phaseYValue" className="mt-2 text-xl font-mono text-teal-400">{phaseY.toFixed(1)}°</span>
                    </div>
                </div>
            </div>

            <div className="w-full bg-gray-800 rounded-xl shadow-inner flex items-center justify-center p-4" style={{ height: '600px', width: '600px' }}>
                <canvas ref={lissajousChartRef}></canvas>
            </div>
        </>
    );
}

export default LissajousApp;
